<!-- Copyright 2022, Common Good Learning Tools LLC -->
<template>
	<k-menu
		bottom
		v-model="menu_showing"
		nudge-right="0"
		nudge-top="-30"
		accessible-popover
		:accessible-label="framework_switcher_menu_label"
		content-id="framework-switcher-panel"
	>
		<template v-slot:activator="{on}">
			<k-btn v-show="btn_size=='small'" ref="trigger_btn" v-on="on" icon :color="color" :style="bstyle" text :accessible-label="framework_switcher_label"><k-icon small>fas fa-arrow-right-arrow-left</k-icon></k-btn>
			<k-btn v-show="btn_size!='small'" v-on="on" :color="color" dark class="k-tight-btn" :small="btn_size=='med'" v-html="large_btn_content"></k-btn>
		</template>
		<k-list dense class="framework_switcher_menu">
			<k-list-item v-if="btn_size=='small'" class="mt-1 mb-3" stylex="border-bottomx:1px solid #ccc; margin-top:-8px;">
				<!-- <k-list-item-icon><k-icon small color="black">fas fa-arrow-right-arrow-left</k-icon></k-list-item-icon> -->
				<k-list-item-title class="text-center"><k-icon small class="mr-2" color="#444">fas fa-arrow-right-arrow-left</k-icon><b style="color:#444">Switch to framework…</b></k-list-item-title>
			</k-list-item>

			<k-list-item class="pb-2">
				<!-- <k-list-item-icon><k-icon small>fas fa-search</k-icon></k-list-item-icon> -->
				<k-list-item-title>
					<!-- this k-autocomplete serves as a search bar -->
					<k-autocomplete label="" outlined dense hide-details prepend-inner-icon="fas fa-search"
						v-model="autocomplete_framework_identifier" 
						:items="all_frameworks"
						@change="$emit('framework_selected', $event);menu_showing=false"
						@click.stop=""
					></k-autocomplete>
				</k-list-item-title>
			</k-list-item>

			<!-- if a default_framework_identifier is sent in, list that item first -->
			<k-list-item v-if="default_framework_record"
				@click="$emit('framework_selected', default_framework_identifier);menu_showing=false"
			>
				<k-list-item-icon><k-icon small>fas fa-map</k-icon></k-list-item-icon>
				<k-list-item-title 
					style="overflow:visible; white-space:normal" 
					:style="default_framework_record==current_selected_framework?'font-weight:bold; font-style:italic;':''" 
					v-html="framework_list_title(default_framework_record)"
				></k-list-item-title>
			</k-list-item>

			<k-menu
				v-for="(cr, index) of category_records"
				:key="index"
				:close-on-content-click="false"
				:transition="false"
				offset-x
				right
				:nudge-top="8"
				:nudge-left="($vuetify.breakpoint.width<700)?0:12"
				style="display: block;"
				accessible-popover
				:accessible-label="framework_category_menu_label(cr)"
				:content-id="framework_category_content_id(cr, index)"
				:parent-menu-active="menu_showing"
			>
				<template v-slot:activator="{on}"><k-hover v-slot:default="{hover}"><k-list-item v-on="on" style="cursor:pointer;" :style="hover?'background-color:#eee':''">
					<k-list-item-icon><k-icon small>fas fa-layer-group</k-icon></k-list-item-icon><k-list-item-title :style="(cr==selected_category||cr.index==-1)?'font-weight:bold; font-style:italic;':''" v-html="cr.title"></k-list-item-title>
					<k-list-item-action class="justify-end"><k-icon small>fas fa-chevron-right</k-icon></k-list-item-action>
				</k-list-item></k-hover></template>

				<k-list dense style="max-height:calc(100vh - 30px); overflow:auto;">
					<k-list-item v-for="(fr) of cr.framework_records" :key="fr.lsdoc_identifier" 
						@click="$emit('framework_selected', fr.lsdoc_identifier);menu_showing=false"
					>
						<k-list-item-icon><k-icon small>fas fa-map</k-icon></k-list-item-icon>
						<k-list-item-title 
							style="overflow:visible; white-space:normal" 
							:style="fr==current_selected_framework?'font-weight:bold; font-style:italic;':''" 
							v-html="framework_list_title(fr)"
						></k-list-item-title>
					</k-list-item>
				</k-list>
			</k-menu>
		</k-list>
	</k-menu>
</template>

<script>
import { mapState, mapGetters } from 'vuex'

export default {
	props: {
		btn_size: { type: String, required: false, default() { return 'large'} },	// small, med, large
		color: { type: String, required: false, default() { return 'brown darken-3'} },
		bstyle: { type: String, required: false, default() { return ''} },
		large_btn_content: { type: String, required: false, default() { return 'BUTTON'} },
		current_selected_framework_identifier: { type: String, required: false, default() { return ''} },
		// by default we show our standard filtered_framework_records list of frameworks, but we can use a different list of frameworks if desired (see, e.g., ResourceSet)
		framework_record_list: { type: Array, required: false, default() { return this.$store.getters.filtered_framework_records} },
		// if default_framework_identifier is supplied, that framework a) will always be included in the switcher, even if it's a sandbox, b) will have 'THIS FRAMEWORK: ' added to the start of the title, and c) will appear first in the drop-down menu (even though it might also appear in one of the categories)
		default_framework_identifier: { type: String, required: false, default() { return ''} },
	},
	data() { return {
		menu_showing: false,
		category_records: [],
		selected_category: null,
		autocomplete_framework_identifier: '',
		// autocomplete_framework_identifier: this.current_selected_framework_identifier,
	}},
	computed: {
		...mapState(['framework_records', 'framework_categories', 'embedded_mode']),
		...mapGetters([]),
		framework_switcher_label() {
			if (this.current_selected_framework?.json?.CFDocument?.title) {
				return `Switch frameworks from ${this.current_selected_framework.json.CFDocument.title}`
			}
			return 'Switch frameworks'
		},
		framework_switcher_menu_label() {
			return `Framework switcher for ${this.framework_noun}s`
		},
		framework_noun() { return this.$store.state.site_config.framework_noun },
		default_framework_record() { return this.framework_records.find(x=>x.lsdoc_identifier == this.default_framework_identifier) },
		current_selected_framework() { 
			return this.framework_records.find(x=>x.lsdoc_identifier == this.current_selected_framework_identifier) 
		},
		all_frameworks() {
			let arr = []
			for (let fr of this.framework_record_list) {
				let doc = fr.json.CFDocument
				// skip crosswalk frameworks and sandboxes, but *don't* skip default_framework_identifier if we received one
				if (doc.identifier != this.default_framework_identifier) {
					if (doc.frameworkType == 'crosswalk') continue
					if (!empty(fr.ss_framework_data.sandboxOfIdentifier)) continue
				}
				arr.push({value: doc.identifier, text: this.framework_list_title(fr)})
			}
			// sort by title
			arr.sort((a,b)=>U.natural_sort(a.text, b.text))
			return arr
		},
	},
	watch: {
		current_selected_framework_identifier() {
			// when framework switches, re-create category_records so that the menus will update to reflect the current framework
			this.create_category_records()
		},
	},
	created() {
		vapp.framework_switcher_component = this
	},
	mounted() {
		// note that this component should not be included anywhere until the document list has been loaded (get_lsdoc_list, which is called immediately upon initializing)
		this.create_category_records()
	},
	methods: {
		framework_category_content_id(category_record, index) {
			return `framework-switcher-category-${index}-${category_record.index}`
		},
		framework_category_menu_label(category_record) {
			return `Frameworks in category ${category_record.title}`
		},
		create_category_records() {
			// this fn is in case_utilities, because we also need it for FrameworkList
			this.category_records = U.create_category_records(this.framework_record_list, false, false)

			// if a framework is already selected,
			if (this.current_selected_framework_identifier) {
				// look for that framework in the category_records
				let cat_index = this.category_records.findIndex(x=>x.framework_records.find(y=>y.lsdoc_identifier == this.current_selected_framework_identifier))
				// if found, store it in this.selected_category and bump that category to the top of the list
				if (cat_index) {
					this.selected_category = this.category_records.splice(cat_index, 1)[0]
					this.category_records.unshift(this.selected_category)
				}
			}

			// add recent category to start of list, if we have any recents
			let cr = U.recent_framework_category(this.framework_record_list, false)
			if (cr.framework_records.length > 0) {
				this.category_records.unshift(cr)
			}
		},

		framework_list_title(fr) {
			if (empty(fr)) return ''
			let s = ''
			if (fr.lsdoc_identifier == this.default_framework_identifier) {
				s = 'THIS FRAMEWORK: '
			}
			s += fr.json.CFDocument.title
			if (!empty(fr.ss_framework_data.sandboxOfIdentifier)) {
				s += ' [SANDBOX]'
			}
			return s
		},
	}
}
</script>

<style lang="scss">
.framework_switcher_menu {
	max-height:calc(100vh - 30px); 
	min-width:300px;
	max-width:90vw;
	overflow:auto; 
	padding-bottom:0;

	.v-list-item {
		min-height:auto!important;
	}
}

</style>
